import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const stats = [
  { id: 1, name: 'Businesses trust us', value: '1', suffix: 'M+' },
  { id: 2, name: 'Transactions Managed', value: '500', prefix: '₹', suffix: 'Cr+' },
  { id: 3, name: 'Platform Uptime', value: '99.9', suffix: '%' },
];

const AnimatedNumber = ({ value, prefix = '', suffix = '' }) => {
  const { ref, inView } = useInView({ threshold: 0.3, triggerOnce: true });
  const [displayValue, setDisplayValue] = useState(0);
  const numericValue = parseFloat(value.replace(/,/g, ''));
  const isDecimal = value.includes('.');

  useEffect(() => {
    if (inView) {
      let start = 0;
      const end = numericValue;
      const duration = 2000;
      
      const timer = setInterval(() => {
        start += (end / (duration / 16));
        if (start >= end) {
          setDisplayValue(end);
          clearInterval(timer);
        } else {
          setDisplayValue(start);
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [inView, numericValue]);

  return (
    <span ref={ref}>
      {prefix}
      {isDecimal ? displayValue.toFixed(1) : Math.floor(displayValue)}
      {suffix}
    </span>
  );
};

const StatsSection = () => {
  return (
    <section className="bg-[#fafafc] py-16 relative overflow-hidden font-sans border-t border-gray-100">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div className="mx-auto max-w-2xl lg:max-w-none">
          
          <div className="grid grid-cols-1 gap-y-12 lg:grid-cols-2 lg:gap-x-12 lg:items-center">
            
            {/* Left Content */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="max-w-xl"
            >
              <h2 className="text-[13px] font-bold uppercase tracking-widest text-[#1e50ff] mb-4">Our Impact</h2>
              <p className="text-3xl font-extrabold tracking-tight text-[#111827] sm:text-4xl lg:text-5xl leading-tight mb-6" style={{ textWrap: 'balance' }}>
                Scale your business with absolute confidence.
              </p>
              <p className="text-base leading-relaxed text-gray-600 font-medium">
                Khatabook clone provides an enterprise-grade experience for small businesses to manage their ledger efficiently. We handle the complexity so you can focus on growth.
              </p>
              
              <div className="mt-8 flex items-center gap-x-4">
                <div className="flex -space-x-3">
                  <img className="w-9 h-9 rounded-full ring-2 ring-[#fafafc] object-cover" src="https://i.pravatar.cc/150?img=1" alt="User" />
                  <img className="w-9 h-9 rounded-full ring-2 ring-[#fafafc] object-cover" src="https://i.pravatar.cc/150?img=2" alt="User" />
                  <img className="w-9 h-9 rounded-full ring-2 ring-[#fafafc] object-cover" src="https://i.pravatar.cc/150?img=3" alt="User" />
                  <div className="w-9 h-9 rounded-full ring-2 ring-[#fafafc] bg-blue-50 flex items-center justify-center text-[11px] font-bold text-blue-600">+9k</div>
                </div>
                <div className="text-[13px] font-semibold text-gray-700">
                  Join growing businesses
                </div>
              </div>
            </motion.div>

            {/* Right Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
              {stats.map((stat, index) => (
                <motion.div 
                  key={stat.id} 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1, type: 'spring', stiffness: 80 }}
                  whileHover={{ y: -4, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.05)" }}
                  className={`flex flex-col justify-center bg-white p-8 rounded-[1.5rem] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.03)] border border-transparent hover:border-blue-50 transition-all duration-300 ${index === 2 ? 'sm:col-span-2 sm:items-center sm:text-center' : ''}`}
                >
                  <dd className="order-first text-4xl font-extrabold tracking-tight text-[#111827] mb-2">
                    <AnimatedNumber value={stat.value} prefix={stat.prefix} suffix={stat.suffix} />
                  </dd>
                  <dt className="text-[13px] font-semibold uppercase tracking-wider text-gray-500">
                    {stat.name}
                  </dt>
                </motion.div>
              ))}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
